import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { useAppDispatch } from '../../store/hooks';
import { requestPasswordReset } from '../../auth/authSlice';
import { LanguageSelector } from '../../components/LanguageSelector';
import { isHostIpOrLoopback, deduceWebIdFromHost } from '../../utils/hostUtils';

/**
 * «Password dimenticata?» (FILO-70): chiede il link di reimpostazione per un indirizzo.
 *
 * Stesso aspetto e stessa risoluzione dell'istanza della pagina di login: il webId si deduce
 * dall'host, e il campo Istanza compare su un host IP/loopback o quando il backend non trova
 * l'istanza dedotta.
 *
 * Dopo l'invio la pagina dice sempre la stessa cosa, anche se l'indirizzo non è registrato:
 * il backend risponde 200 in ogni caso, apposta, e la pagina non deve tradirlo.
 */
export function ForgotPasswordPage() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const host = window.location.hostname;
  const isIpHost = isHostIpOrLoopback(host);

  const [webId, setWebId] = useState(
    isIpHost ? (import.meta.env.VITE_DEFAULT_WEB_ID || '') : (deduceWebIdFromHost(host) || '')
  );
  const [emailAddress, setEmailAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [webIdNotFound, setWebIdNotFound] = useState(false);

  const showWebIdField = isIpHost || webIdNotFound;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    const result = await dispatch(requestPasswordReset({ webId, emailAddress }));
    setLoading(false);
    if (requestPasswordReset.fulfilled.match(result)) {
      setSent(true);
      return;
    }
    const payload = result.payload;
    setWebIdNotFound(payload?.webIdNotFound ?? false);
    setError(payload?.webIdNotFound ? t('auth.errorWebIdRequired') : (payload?.message ?? null));
  }

  return (
    <div className="login-page">
      <div className="login-brand">
        <img src="/logo.svg" alt={t('brand.name')} className="logo" width="100" />
        <h1>
          <span className="login-brand__accent">D'vel</span> CRM
        </h1>
        <p className="login-brand__tagline">{t('brand.tagline')}</p>
      </div>
      <LanguageSelector />
      <form onSubmit={handleSubmit} className="login-form">
        <h2 className="text-center text-lg mb-xl">{t('auth.forgotPasswordTitle')}</h2>

        {sent ? (
          <p className="text-center mb-lg">{t('auth.forgotPasswordSent')}</p>
        ) : (
          <>
            <p className="text-center text-muted text-sm mb-lg">{t('auth.forgotPasswordHint')}</p>

            {error && <div className="text-error text-center mb-lg">{error}</div>}

            {showWebIdField && (
              <TextField label={t('auth.instance')} value={webId} onChange={(e) => setWebId(e.target.value)}
                required fullWidth size="small" className="form-field" />
            )}
            <TextField label={t('auth.usernameOrEmailLabel')} value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)} required autoFocus
              fullWidth size="small" className="form-field" />

            <button type="submit" disabled={loading} className="login-btn">
              {loading ? t('auth.forgotPasswordLoading') : t('auth.forgotPasswordSubmit')}
            </button>
          </>
        )}

        <p className="text-center mt-lg text-sm">
          <Link to="/login" className="text-muted detail-back-link">{t('auth.backToLogin')}</Link>
        </p>
      </form>
    </div>
  );
}
